// FriendOnlineNotifier.js — Notifications de présence des amis. Deux cas DISTINCTS :
//   1) DÉTECTION INITIALE (à TA connexion) : le système regarde qui est DÉJÀ en ligne et t'informe par UNE seule
//      notif résumé — « 🟢 X est en ligne » (un seul) ou « 🟢 Tu as X amis en ligne » (plusieurs). PAS de
//      « vient de se connecter » (ils ne viennent pas de se connecter, ils l'étaient déjà).
//   2) TRANSITION LIVE (un ami se connecte pendant que tu es là) : « 🟢 X vient de se connecter » (batché si ≥3).
//
// AMORÇAGE ROBUSTE = FENÊTRE DE TEMPS (PRIME_MS), pas un simple flag posé après le ping. Pendant la fenêtre, on
// ensemence `notifiedOnline` depuis le ping serveur ET depuis les événements socket (joins), SANS notif individuelle ;
// à la fin de la fenêtre on émet le résumé. → même si le ping initial ÉCHOUE (réseau mobile qui chauffe) ou rate un
// ami, les amis déjà en ligne sont ensemencés par le socket pendant la fenêtre → PLUS de faux « vient de se connecter ».
import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { loadPrefs, getPrefs } from '../services/preferences';
import { onlineFriendIds } from '../services/presence';
import { resolveUser, cachedUser } from '../services/userCache';

const slideIn = keyframes`from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}`;
const Wrap = styled.div`position:fixed;left:1rem;bottom:1rem;z-index:4000;display:flex;flex-direction:column;gap:.5rem;`;
const Toast = styled.div`display:flex;align-items:center;gap:.6rem;background:${theme.colors.bgCard};
  border:1px solid ${theme.colors.borderHover};border-radius:${theme.radii.md};padding:.6rem .9rem;
  box-shadow:${theme.shadows.card};font-size:.85rem;color:${theme.colors.textPrimary};animation:${slideIn} .25s ease;max-width:300px;
  .dot{width:8px;height:8px;border-radius:50%;background:#22c55e;box-shadow:0 0 8px #22c55e;flex-shrink:0;}`;

// ── État de dédup au niveau MODULE (survit aux remontages / navigations) ──
const OFFLINE_MS = 120000;        // 2 min sans présence → vraiment hors ligne (peut re-notifier ensuite)
const PRIME_MS = 6000;            // fenêtre d'amorçage : tout ami détecté ici = « déjà en ligne » (pas « vient de »)
const notifiedOnline = new Set(); // amis déjà comptés « en ligne » → une seule notif par vraie transition
const offTimers = new Map();
const friendName = new Map();     // id -> nom AppStore (display_name), pour des toasts propres
let primeStart = 0;               // début de la fenêtre d'amorçage (0 = pas encore démarrée)
let followWired = false;          // suivi/souscription établis une seule fois par session
let summaryShown = false;         // la notif résumé initiale n'est montrée qu'UNE fois par session
function isPriming() { return primeStart === 0 || (Date.now() - primeStart) < PRIME_MS; }
export function _resetFriendNotifier() {
  notifiedOnline.clear(); offTimers.forEach((t) => clearTimeout(t)); offTimers.clear(); friendName.clear();
  primeStart = 0; followWired = false; summaryShown = false;
}

// Nom AppStore d'un ami (jamais un username moche) : map amis → cache → repli, déclenche la résolution si absent.
function nameOf(id) {
  const n = friendName.get(id) || (cachedUser(id) && cachedUser(id).name);
  if (!n) resolveUser(id);
  return n || 'Un ami';
}

export default function FriendOnlineNotifier() {
  const { auth, isLoggedIn } = useAuth();
  const [toasts, setToasts] = useState([]);

  const pushToast = useCallback((el) => {
    if (!getPrefs().notif.friendOnline) return;   // respecte la préférence utilisateur
    const tid = 'n' + Date.now() + Math.random();
    setToasts((prev) => [...prev.slice(-3), { id: tid, el }]);   // au plus 4 empilés
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== tid)), 6000);
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !auth.enabled) { _resetFriendNotifier(); return undefined; }
    let alive = true;
    loadPrefs();
    const myId = (auth.session && auth.session.user_id) || null;
    const myName = (auth.session && auth.session.username) || null;
    let summaryTimer = null;

    // BATCH des connexions LIVE : on regroupe les joins arrivés dans une courte fenêtre pour éviter 10 toasts.
    let liveBatch = [];
    let liveTimer = null;
    const flushLive = () => {
      liveTimer = null;
      const batch = liveBatch; liveBatch = [];
      if (!batch.length || isPriming()) return;
      if (batch.length >= 3) pushToast(<span>🟢 <strong>{batch.length}</strong> amis viennent de se connecter</span>);
      else batch.forEach((id) => pushToast(<span>🟢 <strong>{nameOf(id)}</strong> vient de se connecter</span>));
    };

    // RÉSUMÉ initial (émis à la fin de la fenêtre d'amorçage) : tout ami détecté pendant la fenêtre = « déjà en ligne ».
    const emitSummary = () => {
      if (summaryShown) return; summaryShown = true;
      const arr = [...notifiedOnline].filter((id) => friendName.has(id));
      if (!arr.length || !getPrefs().notif.friendOnline) return;
      if (arr.length === 1) pushToast(<span>🟢 <strong>{nameOf(arr[0])}</strong> est en ligne</span>);
      else pushToast(<span>🟢 Tu as <strong>{arr.length}</strong> amis en ligne</span>);
    };

    // Souscription présence (joins/leaves temps réel).
    const unsub = auth.onStatusPresence((ev) => {
      (ev.joins || []).forEach((p) => {
        const id = p.user_id || p.username; if (!id) return;
        if (id === myId || (p.username && p.username === myName)) return;   // jamais pour SA propre connexion
        const t = offTimers.get(id); if (t) { clearTimeout(t); offTimers.delete(id); }   // un join annule le hors-ligne
        if (notifiedOnline.has(id)) return;               // déjà en ligne (joins répétés) → aucune re-notif
        notifiedOnline.add(id);
        if (isPriming()) return;                          // fenêtre d'amorçage → ensemencé SILENCIEUSEMENT (déjà en ligne)
        // Vraie transition hors-ligne→en-ligne pendant ta session → batché.
        liveBatch.push(id); resolveUser(id);
        if (!liveTimer) liveTimer = setTimeout(flushLive, 2500);
      });
      (ev.leaves || []).forEach((p) => {
        const id = p.user_id || p.username; if (!id || !notifiedOnline.has(id) || offTimers.has(id)) return;
        offTimers.set(id, setTimeout(() => { notifiedOnline.delete(id); offTimers.delete(id); }, OFFLINE_MS));
      });
    });

    if (!followWired) {
      followWired = true;
      primeStart = Date.now();                              // démarre la fenêtre d'amorçage
      summaryTimer = setTimeout(emitSummary, PRIME_MS);     // résumé à la fin de la fenêtre (ensemencé par ping + socket)
      (async () => {
        // 1) Amis + carte des noms AppStore.
        let ids = [], fset = new Set();
        try {
          const fr = await auth.listFriends(0);
          (fr || []).forEach((f) => { const u = f.user; const id = u && (u.id || u.user_id); if (id) friendName.set(id, u.display_name || u.username || 'Un ami'); });
          ids = (fr || []).map((f) => f.user && (f.user.id || f.user.user_id)).filter(Boolean);
          fset = new Set(ids);
          // 2) SUIVI de présence : la RÉPONSE de followUsers contient les présences ACTUELLES des amis suivis →
          //    C'EST elle qui donne les amis DÉJÀ en ligne (un ami déjà connecté n'émet AUCUN « join » socket, qui ne
          //    fire que sur les CHANGEMENTS). On ensemence donc `notifiedOnline` depuis ce retour → « X est en ligne ».
          if (ids.length) {
            const pres = await auth.followUsers(ids);
            (pres || []).forEach((p) => { const id = p && (p.user_id || p.username); if (id && fset.has(id)) notifiedOnline.add(id); });
          }
        } catch (e) { /* */ }
        if (!alive) return;
        // 3) Ping serveur en SECOURS pendant la fenêtre (au cas où followUsers raterait un ami en arrière-plan).
        for (let i = 0; i < 4 && alive && isPriming(); i++) {
          const online = await onlineFriendIds();          // Array (même vide) = succès ; null = RPC échoué → on réessaie
          if (Array.isArray(online)) { online.forEach((id) => { if (fset.has(id)) notifiedOnline.add(id); }); }
          await new Promise((r) => setTimeout(r, 1000));
        }
      })();
    }

    return () => { alive = false; if (unsub) unsub(); if (liveTimer) clearTimeout(liveTimer); if (summaryTimer) clearTimeout(summaryTimer); };
  }, [auth, isLoggedIn, pushToast]);

  if (!toasts.length) return null;
  return (
    <Wrap>
      {toasts.map((t) => (
        <Toast key={t.id}><span className="dot" />{t.el}</Toast>
      ))}
    </Wrap>
  );
}
